import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, IconButton, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, AppBar, Toolbar,
  InputAdornment, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, Switch, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, QrCode as QrCodeIcon,
} from '@mui/icons-material';
import api from '../api/api';

const Salas = () => {
  const [salas, setSalas] = useState([]);
  const [originalSalas, setOriginalSalas] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSala, setSelectedSala] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    nombre: '',
    id_sucursal: '',
    activo: true,
  });

  const handleImprimirQR = async (salaId) => {
  try {
    const response = await api.get(`/salas/qr/pdf/${salaId}`, { responseType: 'blob' });
    
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `QR_SALA_${salaId}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
    
    showSnackbar('PDF QR generado correctamente');
  } catch (err) {
    showSnackbar('Error al generar QR', 'error');
  }
};

  // Obtener datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Cargar Sucursales para el select del modal
        const sucursalesRes = await api.get('/sucursales/');
        setSucursales(Array.isArray(sucursalesRes.data) ? sucursalesRes.data : []);

        // Cargar Salas
        const salasRes = await api.get('/salas/');
        const data = Array.isArray(salasRes.data) ? salasRes.data : [];
        setSalas(data);
        setOriginalSalas(data);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar salas:', err);
        showSnackbar('Error al cargar los datos', 'error');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Buscador
  useEffect(() => {
    const filtered = originalSalas.filter(s => 
      s.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setSalas(filtered);
  }, [searchTerm, originalSalas]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenModal = (sala = null) => {
    setSelectedSala(sala);
    if (sala) {
      setFormData({
        nombre: sala.nombre,
        id_sucursal: sala.id_sucursal || '',
        activo: sala.activo ?? true,
      });
    } else {
      setFormData({ nombre: '', id_sucursal: '', activo: true });
    }
    setOpenModal(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.nombre) return showSnackbar('El nombre es obligatorio', 'error');

      if (selectedSala) {
        await api.put(`/salas/${selectedSala.id}`, formData);
        showSnackbar('Sala actualizada correctamente');
      } else {
        await api.post('/salas/', formData);
        showSnackbar('Sala creada correctamente');
      }
      
      // Recargar datos
      const res = await api.get('/salas/');
      setOriginalSalas(res.data);
      setOpenModal(false);
    } catch (err) {
      showSnackbar('Error al guardar: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sala?')) return;
    try {
      await api.delete(`/salas/${id}`);
      setOriginalSalas(originalSalas.filter(s => s.id !== id));
      showSnackbar('Sala eliminada');
    } catch (err) {
      showSnackbar('Error al eliminar', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#334155', mb: 3, borderRadius: 1 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Gestión de Salas Ubicaciones</Typography>
          <TextField
            variant="outlined" size="small" placeholder="Buscar sala..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} disableGutters>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal()}>
            Nueva Sala
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell align="center"><b>ID</b></TableCell>
                  <TableCell><b>Nombre de la Sala</b></TableCell>
                  <TableCell><b>Sucursal</b></TableCell>
                  <TableCell align="center"><b>Estado</b></TableCell>
                  <TableCell align="center"><b>Acciones</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salas.map((sala) => (
                  <TableRow key={sala.id}>
                    <TableCell align="center">{sala.id}</TableCell>
                    <TableCell><b>{sala.nombre}</b></TableCell>
                    <TableCell>
                      {sucursales.find(suc => suc.id_sucursal === sala.id_sucursal)?.nombre_sucursal || 'Sin sucursal'}
                    </TableCell>
                    <TableCell align="center">
                      <Typography color={sala.activo ? 'success.main' : 'error.main'}>
                        {sala.activo ? 'Activo' : 'Inactivo'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleOpenModal(sala)} color="primary"><EditIcon /></IconButton>
                      <IconButton onClick={() => handleImprimirQR(sala.id)} color="secondary"> <QrCodeIcon /> </IconButton>
                      <IconButton onClick={() => handleDelete(sala.id)} color="error"><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>

      {/* Modal Crear/Editar */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="xs">
        <DialogTitle>{selectedSala ? 'Editar Sala' : 'Nueva Sala'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nombre de la Sala" fullWidth required sx={{ mb: 2, mt: 1 }}
            value={formData.nombre}
            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          />
          <TextField
            select label="Sucursal" fullWidth
            value={formData.id_sucursal}
            onChange={(e) => setFormData({ ...formData, id_sucursal: e.target.value })}
            sx={{ mb: 2 }}
          >
            {sucursales.map((suc) => (
              <MenuItem key={suc.id_sucursal} value={suc.id_sucursal}>
                {suc.nombre_sucursal}
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Switch
              checked={formData.activo}
              onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
            />
            <Typography>Sala Activa</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
          <Button onClick={handleSave} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Salas;